import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Page, PageHeader } from '../components/layout/AppShell';
import { Card, CardHeader, DetailList, DetailRow } from '../components/ui/Card';
import { DataTable, TableToolbar, Pagination } from '../components/ui/DataTable';
import { AsyncBoundary, EmptyState, SkeletonRows, Skeleton } from '../components/ui/AsyncBoundary';
import { SearchInput, Select } from '../components/ui/Field';
import { Button, SegmentedControl } from '../components/ui/Button';
import { Badge, HealthMeter } from '../components/ui/Badge';
import { Icon } from '../components/ui/Icon';
import { deviceColumns } from '../components/domain/columns';
import { HealthBadge, LastSeen, VehicleIdentity } from '../components/domain/VehicleBits';
import {
  ProvisionDeviceModal, AssignDeviceModal, AllocateDeviceModal, RotateSecretModal, RawPacketsModal,
  ResetDeviceModal,
} from '../components/domain/DeviceModals';
import { DeviceCommands } from '../components/domain/DeviceCommands';
import { Can } from '../auth/Can';
import { useAuth } from '../auth/AuthProvider';
import { useResource, usePagedList } from '../hooks/useResource';
import { healthBand, healthReasons } from '../lib/domain';
import * as fmt from '../lib/format';

/** The TCU inventory. */
export function Devices() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [status, setStatus] = useState('');
  const [provisioning, setProvisioning] = useState(false);

  // Searched and paged on the server. The inventory is the list most likely to
  // reach five figures — every device the platform has ever provisioned lives
  // here, fitted or spare.
  const devices = usePagedList(
    '/api/devices',
    { status: status || undefined },
    { limit: 50, pollMs: 60_000 }
  );
  const orgs = useResource(can('org:write') ? '/api/orgs' : null);

  return (
    <Page className="space-y-4">
      <PageHeader
        title="Devices"
        subtitle="Telematics control units and what they are fitted to"
        actions={
          <Can do="device:provision">
            <Button variant="accent" size="sm" icon="plus" onClick={() => setProvisioning(true)}>
              Provision device
            </Button>
          </Can>
        }
      />

      <Card className="overflow-hidden">
        <TableToolbar>
          <SearchInput
            value={devices.query}
            onChange={devices.setQuery}
            placeholder="IMEI or serial"
            className="w-full sm:w-72"
          />
          <SegmentedControl
            value={status}
            onChange={setStatus}
            options={[
              { value: '', label: 'All' },
              { value: 'active', label: 'Fitted' },
              { value: 'unassigned', label: 'Spare' },
            ]}
          />
          <span className="ml-auto text-[12.5px] text-ink-3 tabular-nums">
            {devices.total.toLocaleString()} device{devices.total === 1 ? '' : 's'}
          </span>
        </TableToolbar>

        <AsyncBoundary
          state={devices}
          skeleton={<SkeletonRows rows={8} />}
          isEmpty={() => !devices.rows.length}
          empty={
            <EmptyState
              icon="chip"
              title="No devices in your scope"
              description="Devices appear here once they are allocated to your organisation."
            />
          }
        >
          {() => (
            <>
              <DataTable
                columns={deviceColumns()}
                rows={devices.rows}
                onRowClick={(row) => navigate(`/devices/${row.id}`)}
                defaultSort={{ key: 'health_score', direction: 'asc' }}
                paged={devices.total > devices.page.limit}
              />
              <Pagination page={devices.page} onChange={devices.setOffset} unit="devices" />
            </>
          )}
        </AsyncBoundary>
      </Card>

      <ProvisionDeviceModal
        open={provisioning}
        onClose={() => setProvisioning(false)}
        organizations={orgs.data?.organizations || []}
        onCreated={devices.refetch}
      />
    </Page>
  );
}

/** One device: health, assignment history and its own event timeline. */
export function DeviceDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const [assigning, setAssigning] = useState(false);
  const [allocating, setAllocating] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [inspecting, setInspecting] = useState(false);
  const [resetting, setResetting] = useState(false);
  // Bumped when a command is queued, so the history below refetches at once
  // rather than waiting out its poll interval — pressing a button and seeing
  // nothing happen is how an operator decides the button is broken.
  const [commandNonce, setCommandNonce] = useState(0);

  const device = useResource(`/api/devices/${id}`, null, { pollMs: 30_000 });
  const orgs = useResource(can('device:allocate') ? '/api/orgs' : null);

  return (
    <Page className="space-y-4">
      <AsyncBoundary state={device} skeleton={<Skeleton className="h-96 w-full" />}>
        {(data) => {
          const d = data.device;
          const state = data.state;
          const band = healthBand({ score: state?.health_score, online: state?.online });
          const reasons = healthReasons(state?.health_flags);

          return (
            <>
              <PageHeader
                breadcrumb={
                  <Link
                    to="/devices"
                    className="mb-1 inline-flex items-center gap-1 text-[12.5px] text-ink-3 hover:text-ink-2"
                  >
                    <Icon name="chevronLeft" size="xs" />
                    Devices
                  </Link>
                }
                title={d.imei || 'Device'}
                subtitle={[d.hardware_model, d.vendor_id].filter(Boolean).join(' · ')}
                actions={
                  <div className="flex flex-wrap items-center gap-2">
                    <HealthBadge score={state?.health_score} online={state?.online} flags={state?.health_flags} />
                    <LastSeen at={state?.last_packet_at || d.last_seen_at} online={state?.online} />
                    <Can do="device:command">
                      <Button variant="ghost" size="sm" icon="refresh" onClick={() => setResetting(true)}>
                        Reset
                      </Button>
                    </Can>
                    <Can do="telemetry:raw">
                      <Button variant="ghost" size="sm" icon="table" onClick={() => setInspecting(true)}>
                        Raw packets
                      </Button>
                    </Can>
                    <Can do="device:secrets">
                      <Button variant="ghost" size="sm" icon="shield" onClick={() => setRotating(true)}>
                        Rotate secret
                      </Button>
                    </Can>
                    <Can do="device:allocate">
                      <Button variant="secondary" size="sm" icon="building" onClick={() => setAllocating(true)}>
                        Allocate
                      </Button>
                    </Can>
                    <Can do="device:assign">
                      <Button variant="secondary" size="sm" icon="truck" onClick={() => setAssigning(true)}>
                        {d.vehicle_id ? 'Move device' : 'Fit to vehicle'}
                      </Button>
                    </Can>
                  </div>
                }
              />

              <div className="grid gap-4 xl:grid-cols-[1fr_1.4fr]">
                <div className="space-y-4">
                  <Card>
                    <CardHeader title="Device" icon="chip" />
                    <div className="px-4 py-2">
                      <DetailList>
                        <DetailRow label="IMEI" value={d.imei} mono />
                        <DetailRow label="Serial" value={d.serial} mono />
                        <DetailRow label="ICCID" value={d.iccid} mono />
                        <DetailRow label="Firmware" value={d.firmware_version} mono />
                        <DetailRow label="Hardware" value={d.hardware_model} />
                        <DetailRow
                          label="Status"
                          value={
                            <Badge tone={d.status === 'ACTIVE' ? 'good' : 'neutral'} size="sm" icon={null}>
                              {String(d.status).toLowerCase()}
                            </Badge>
                          }
                        />
                        <DetailRow
                          label="Fitted to"
                          value={
                            d.vehicle_id ? (
                              <VehicleIdentity
                                vehicle={{ id: d.vehicle_id, reg_number: d.reg_number }}
                                showFleet={false}
                              />
                            ) : (
                              'Spare'
                            )
                          }
                        />
                      </DetailList>
                    </div>
                  </Card>

                  <Card>
                    <CardHeader title="Health" subtitle="Seven weighted signals" icon="shield" />
                    <div className="space-y-3 p-4">
                      <HealthMeter
                        score={state?.health_score}
                        tone={band.tone}
                        label={`Overall — ${band.label}`}
                      />
                      {reasons.length ? (
                        <ul className="space-y-1.5 border-t border-line pt-3">
                          {reasons.map((reason) => (
                            <li key={reason.key} className="flex items-center justify-between gap-2">
                              <span className="text-[13px] text-ink-2">{reason.label}</span>
                              <Badge tone={reason.tone} size="sm">
                                {reason.level}
                              </Badge>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="border-t border-line pt-3 text-[13px] text-ink-3">
                          Every signal is within range.
                        </p>
                      )}
                    </div>
                  </Card>
                </div>

                <div className="space-y-4">
                  <Card className="overflow-hidden">
                    <CardHeader title="Assignment history" subtitle="Which vehicle, and when" icon="truck" />
                    {data.assignments?.length ? (
                      <ul className="divide-y divide-line">
                        {data.assignments.map((assignment) => (
                          <li key={assignment.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                            <div className="min-w-0">
                              <p className="truncate text-[13.5px] font-medium text-ink">
                                {assignment.reg_number || 'Removed vehicle'}
                              </p>
                              <p className="text-[12px] text-ink-3">
                                {fmt.date(assignment.assigned_at)} —{' '}
                                {assignment.unassigned_at ? fmt.date(assignment.unassigned_at) : 'present'}
                              </p>
                            </div>
                            {!assignment.unassigned_at && (
                              <Badge tone="good" size="sm" icon={null}>
                                Current
                              </Badge>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <EmptyState icon="truck" title="Never fitted" description="This device is still a spare." />
                    )}
                  </Card>

                  <DeviceCommands deviceId={d.id} nonce={commandNonce} />

                  <Card className="overflow-hidden">
                    <CardHeader title="Device events" icon="history" />
                    {data.events?.length ? (
                      <ul className="max-h-96 divide-y divide-line overflow-y-auto">
                        {data.events.map((event) => (
                          <li key={event.id} className="flex items-baseline justify-between gap-3 px-4 py-2">
                            <span className="text-[13px] text-ink">{event.type.replace(/_/g, ' ')}</span>
                            <span className="shrink-0 text-[11.5px] text-ink-3" title={fmt.dateTime(event.at)}>
                              {fmt.relativeTime(event.at)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <EmptyState icon="history" title="No events recorded" />
                    )}
                  </Card>
                </div>
              </div>

              <AssignDeviceModal
                open={assigning}
                onClose={() => setAssigning(false)}
                device={d}
                onAssigned={() => {
                  setAssigning(false);
                  device.refetch();
                }}
              />

              <AllocateDeviceModal
                open={allocating}
                onClose={() => setAllocating(false)}
                device={d}
                organizations={orgs.data?.organizations || []}
                onAllocated={() => {
                  setAllocating(false);
                  device.refetch();
                }}
              />

              <RotateSecretModal
                open={rotating}
                onClose={() => setRotating(false)}
                device={d}
              />

              <ResetDeviceModal
                open={resetting}
                onClose={() => setResetting(false)}
                device={d}
                onQueued={() => setCommandNonce((n) => n + 1)}
              />

              <RawPacketsModal
                open={inspecting}
                onClose={() => setInspecting(false)}
                device={d}
              />
            </>
          );
        }}
      </AsyncBoundary>
    </Page>
  );
}
